/**
 * Occupational Therapy Visit Note (Follow-Up) — options + empty payload.
 * After shared common header (1–6): Reason → Subjective → Objective →
 * Interventions → Response → Functional Status → Goals → Plan
 * (per product screenshots).
 */

import { emptyPtNotesCommonHeader } from "../ptNotes/ptNotesCommonHeaderUtils";
import { emptyTextGoals } from "../ptNotes/ptGoals";

export { PAIN_LEVELS } from "../ptNotes/ptClinicalVitals";

export const YES_NO_OPTIONS = ["Yes", "No"];

export const YES_NO_SOME_OPTIONS = ["Yes", "No", "Some"];

export const VISIT_TYPE_OPTIONS = ["Skilled", "Reassessment"];

export const REASON_FOR_VISIT_OPTIONS = [
  "Routine Follow-Up",
  "Progress Check",
  "Reassessment",
  "Other",
];

export const OT_VISIT_ADL_TASKS = [
  "Bathing",
  "Dressing",
  "Grooming",
  "Feeding",
  "Toileting",
];

export const OT_VISIT_ADL_SCORE_OPTIONS = [
  "Ind",
  "Mod-I",
  "Sup",
  "CGA",
  "Min-A",
  "Mod-A",
  "Max-A",
  "Dep",
];

export const OT_VISIT_UE_METRICS = ["AROM", "MMT", "Coordination"];

export const OT_VISIT_UE_DEFAULTS = {
  AROM: { right: "WNL/WFL", left: "WNL/WFL" },
  MMT: { right: "5/5", left: "5/5" },
  Coordination: { right: "Intact", left: "Intact" },
};

export const emptyOtVisitUeRows = () =>
  OT_VISIT_UE_METRICS.map((metric) => ({
    metric,
    right: OT_VISIT_UE_DEFAULTS[metric]?.right || "",
    left: OT_VISIT_UE_DEFAULTS[metric]?.left || "",
  }));

export const emptyOtVisitAdlScores = () =>
  Object.fromEntries(OT_VISIT_ADL_TASKS.map((task) => [task, "Ind"]));

export const COGNITION_STATUS_OPTIONS = ["Intact", "Impaired"];

export const OT_VISIT_INTERVENTION_OPTIONS = [
  "ADL Retraining",
  "Fine Motor Skills",
  "Therapeutic Exercise",
  "Education",
  "IADL Retraining",
  "Cognitive Retraining",
  "Energy Conservation",
  "Other",
];

export const PATIENT_RESPONSE_OPTIONS = ["Good", "Fair", "Poor"];

export const PROGRESS_TOWARD_GOALS_OPTIONS = [
  "On Track",
  "Progressing",
  "Plateau",
  "Regression",
];

export const MOBILITY_TREND_OPTIONS = ["Improved", "Same", "Declined"];

export const TRANSFER_STATUS_OPTIONS = [
  "Independent",
  "Modified Independent",
  "Supervision",
  "CGA",
  "Min Assist",
  "Mod Assist",
  "Max Assist",
  "Dependent",
];

export const AMBULATION_STATUS_OPTIONS = [
  "Independent",
  "With Device (Supervision)",
  "With Device (CGA)",
  "Min Assist",
  "Mod Assist",
  "Max Assist",
  "Non-ambulatory",
];

export const ADL_IMPACTED_OPTIONS = [
  "Bathing",
  "Dressing",
  "Toileting",
  "Stairs",
];

export const HOME_SAFETY_OPTIONS = [
  "Safe",
  "Needs Improvement",
  "Needs Education",
];

export const PAIN_SELECT_OPTIONS = [
  { value: 0, label: "0 - No Pain" },
  { value: 1, label: "1" },
  { value: 2, label: "2" },
  { value: 3, label: "3" },
  { value: 4, label: "4" },
  { value: 5, label: "5 - Moderate" },
  { value: 6, label: "6" },
  { value: 7, label: "7" },
  { value: 8, label: "8" },
  { value: 9, label: "9" },
  { value: 10, label: "10 - Worst" },
];

export const toggleInList = (list, item) => {
  const arr = Array.isArray(list) ? list : [];
  return arr.includes(item) ? arr.filter((x) => x !== item) : [...arr, item];
};

export const coerceExclusiveChoice = (value) => {
  if (Array.isArray(value)) return value.find(Boolean) || "";
  return value == null ? "" : String(value);
};

export const parsePainLevel = (raw) => {
  if (raw == null || raw === "") return null;
  const n = Number(raw);
  return Number.isFinite(n) && n >= 0 && n <= 10 ? n : null;
};

/** Full editable OT Visit Note payload (JSON on dedicated row). */
export const emptyOtVisitPayload = () => ({
  billingCode: "",
  timeIn: "",
  timeOut: "",

  visitDate: "",
  commonHeader: emptyPtNotesCommonHeader(),

  visitTypes: [],

  // —— 7. Reason for Visit / Since Last Visit ——
  reasonForVisit: [],
  reasonForVisitOther: "",
  sinceLastVisit: "",

  // —— 8. Subjective Report ——
  subjectiveStatement: "",
  painPreVisit: null,
  painPostVisit: null,
  painLocation: "",

  // —— 9. Objective ——
  adlScores: emptyOtVisitAdlScores(),
  ueFunction: emptyOtVisitUeRows(),
  cognitionStatus: "",
  cognitionNotes: "",

  // —— 10. Interventions Provided ——
  interventions: [],
  interventionOther: "",
  interventionDetails: "",

  // —— 11. Patient Response / Progress ——
  patientResponse: "",
  progressTowardGoals: "",
  functionalImprovement: "",
  responseComments: "",

  // —— 12. Current Functional Status ——
  mobilityTrend: "",
  transfersStatus: "",
  ambulationStatus: "",
  adlsImpacted: [],
  homeSafety: [],

  // —— 13. Goals ——
  shortTermGoals: emptyTextGoals(3, "st"),
  longTermGoals: emptyTextGoals(3, "lt"),

  // —— 14. Plan / Treatment Plan ——
  continueOt: "",
  frequency: "",
  duration: "",
  nextVisitDate: "",
  interventionsPlanned: "",
});

export const OT_VISIT_ARRAY_KEYS = [
  "visitTypes",
  "reasonForVisit",
  "interventions",
  "adlsImpacted",
  "homeSafety",
];
